import { LuTicket } from 'react-icons/lu';
import { useExternalTicketFlow } from '../components/events/ExternalTicketFlow';
import { createFileRoute, Link, useNavigate, Outlet, useRouterState } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import { Header } from '../components/layout/Header';
import { BottomNav } from '../components/layout/BottomNav';
import { Button } from '../components/ui/Button';
import { EventCheckInModal } from '../components/events/EventCheckInModal';
import { ManagePrivateAccessModal } from '../components/events/ManagePrivateAccessModal';
import { ManageCollaboratorsModal } from '../components/events/ManageCollaboratorsModal';
import { ShareEventModal } from '../components/events/ShareEventModal';
import { AuthGateModal } from '../components/common/AuthGateModal';
import { TicketSelectionCard } from '../components/events/TicketSelectionCard';
import { TicketSelectionModal } from '../components/events/TicketSelectionModal';
import { OrderSummaryCard } from '../components/events/OrderSummaryCard';
import { useTimezoneLabel } from '../api/timezones';
import { EventInterestCard } from '../components/events/EventInterest';
import { EventOrganizerCard } from '../components/events/EventOrganizerCard';
import {
  useGetEventDetail,
  useGetPublicEventDetail,
  useGetSubEvents,
  getEventLocation,
  getEventCoverImage,
  isEventExpired,
  getCollaboratorEventType,
  type EventItem,
} from '../api/events';
import {
  cancelActiveCheckoutSession,
  clearStoredCheckoutCustomer,
  clearStoredCheckoutSession,
  getStoredCheckoutCustomer,
  getStoredCheckoutSession,
  MAX_TICKETS_PER_PURCHASE,
} from '../api/checkout';
import { useAuth } from '../hooks/useAuth';
import { useEventAccentColor } from '../hooks/useEventAccentColor';
import { canEditEvent } from '../utils/eventPermissions';
import { getTicketSalesStatus, type TicketSalesStatus } from '../utils/eventTickets';
import { parseWKTPoint } from '../utils/eventGeo';
import {
  FiArrowLeft,
  FiArrowRight,
  FiCalendar,
  FiCheckSquare,
  FiMapPin,
  FiMusic,
  FiAlertCircle,
  FiChevronLeft,
  FiChevronRight,
  FiLock,
  FiEdit2,
  FiX,
  FiExternalLink,
  FiUsers,
  FiList,
  FiShare2,
} from 'react-icons/fi';
import { GOOGLE_MAPS_API_KEY } from '../services/googleMapsLoader';
import { getProfileIdentifier } from '../utils/profileUrl';
import { formatDate, formatTime, formatDateOrdinal } from '../utils/eventDate';
import { LoadingLogo } from '../components/common/LoadingLogo';
import { Avatar } from '../components/ui/Avatar';
import locationDirectionIcon from '../assets/location_direction.svg';

const EVENT_IMAGE_FALLBACK_SRC = '/Musicade-Logo.png';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || window.location.origin;

function getEventShareUrl(ev: EventItem): string {
  const identifier = ev.event_uuid || ev.id;
  return `${API_BASE_URL.replace(/\/+$/, '')}/embedded_checkout_page/${identifier}`;
}

export const Route = createFileRoute('/events/$eventId')({
  component: EventDetail,
});

// ─── Component ────────────────────────────────────────────────────────────────

function EventDetail() {
  const { eventId } = Route.useParams();
  const navigate = useNavigate();
  const routerState = useRouterState();
  const { user, isAuthenticated } = useAuth();
  const [imageIndex, setImageIndex] = useState(0);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [ticketCart, setTicketCart] = useState<Record<number, number>>({});
  const [isCheckInOpen, setIsCheckInOpen] = useState(false);
  const [isManagePrivateAccessOpen, setIsManagePrivateAccessOpen] = useState(false);
  const [isManageCollaboratorsOpen, setIsManageCollaboratorsOpen] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [activeSession, setActiveSession] = useState<{
    sessionId: string;
    expiresAt: string | null;
  } | null>(null);
  const [isCancellingSession, setIsCancellingSession] = useState(false);
  const [isCreateAuthGateOpen, setIsCreateAuthGateOpen] = useState(false);
  const [isTicketModalOpen, setIsTicketModalOpen] = useState(false);

  // Use authenticated endpoint for logged-in users, public endpoint for guests
  const authEventResult = useGetEventDetail(isAuthenticated ? eventId : undefined);
  const publicEventResult = useGetPublicEventDetail(!isAuthenticated ? eventId : undefined);
  const { data: event, isLoading, isError } = isAuthenticated ? authEventResult : publicEventResult;
  const externalTicketFlow = useExternalTicketFlow(event);
  const [markedInterested, setMarkedInterested] = useState(false);
  const getTimezoneLabel = useTimezoneLabel();

  // Sub-events — only fetched when the parent is a multi_event
  const isMultiEvent = event?.event_structure_type === 'multi_event' && !event?.is_sub_event;
  const { data: subEvents, isLoading: subEventsLoading } = useGetSubEvents(event?.id, isMultiEvent);
  const imageMedias = (event?.medias ?? []).filter(
    (m) => m.media_type === 'image' || m.image || m.file_url,
  );
  const images = imageMedias.map((m) => m.file_url ?? m.image ?? m.file ?? '').filter(Boolean);
  const eventAccent = useEventAccentColor(event?.dominant_color);

  useEffect(() => {
    setImageIndex(0);
    setTicketCart({});
  }, [eventId]);

  useEffect(() => {
    if (images.length === 0) return;
    setImageIndex((prev) => (prev >= images.length ? 0 : prev));
  }, [images.length]);

  const getEditRoute = (
    ev: EventItem,
  ): '/events/$eventId/edit' | '/events/$eventId/edit-external' => {
    const isExternal = ev.event_ticketing_type === 'external' || Boolean(ev.external_ticketing_url);
    return isExternal ? '/events/$eventId/edit-external' : '/events/$eventId/edit';
  };

  const handleTouchStart = (e: React.TouchEvent) => setTouchStart(e.touches[0].clientX);
  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStart === null || images.length <= 1) return;
    const diff = touchStart - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) {
      if (diff > 0) setImageIndex((i) => (i + 1) % images.length);
      else setImageIndex((i) => (i - 1 + images.length) % images.length);
    }
    setTouchStart(null);
  };

  const handleEdit = () => {
    if (!event) return;
    navigate({ to: getEditRoute(event), params: { eventId: String(event.id) } });
  };

  const handleShare = () => {
    if (!event) return;
    setIsShareModalOpen(true);
  };

  const addToCart = (ticketId: number) => {
    setTicketCart((cur) => {
      const totalInCart = Object.values(cur).reduce((sum, qty) => sum + qty, 0);
      if (totalInCart >= MAX_TICKETS_PER_PURCHASE) return cur;
      const ticket = (event?.tickets ?? []).find((t) => t.id === ticketId);
      const currentQty = cur[ticketId] ?? 0;
      if (
        ticket &&
        typeof ticket.remaining_tickets === 'number' &&
        currentQty >= ticket.remaining_tickets
      ) {
        return cur;
      }
      return { ...cur, [ticketId]: currentQty + 1 };
    });
  };

  const removeFromCart = (ticketId: number) => {
    setTicketCart((cur) => {
      const next = Math.max(0, (cur[ticketId] ?? 0) - 1);
      if (next === 0) {
        const rest = { ...cur };
        delete rest[ticketId];
        return rest;
      }
      return { ...cur, [ticketId]: next };
    });
  };

  const handleProceedToCheckout = () => {
    if (!event) return;
    const ticketsParam = Object.entries(ticketCart)
      .filter(([, qty]) => qty > 0)
      .map(([id, qty]) => `${id}:${qty}`)
      .join(',');
    navigate({
      to: '/events/$eventId/checkout',
      params: { eventId: String(event.id) },
      search: ticketsParam ? { tickets: ticketsParam } : {},
    });
  };

  // On page load: detect stored session but do NOT auto-open checkout
  useEffect(() => {
    if (!event || event.event_ticketing_type === 'external') return;

    const { sessionId, expiresAt } = getStoredCheckoutSession();
    const hasInternalTickets = (event.tickets ?? []).some((ticket) => !ticket.is_archived);

    if (!sessionId || !hasInternalTickets) return;

    // Clear if already expired locally
    if (expiresAt) {
      const expiry = new Date(expiresAt);
      if (!Number.isNaN(expiry.getTime()) && expiry.getTime() <= Date.now()) {
        clearStoredCheckoutSession();
        return;
      }
    }

    setActiveSession({ sessionId, expiresAt });
  }, [event]);

  const handleResumeSession = () => {
    if (!event) return;
    navigate({ to: '/events/$eventId/checkout', params: { eventId: String(event.id) } });
  };

  const handleCancelActiveSession = async () => {
    if (!activeSession || isCancellingSession) return;
    setIsCancellingSession(true);
    const { email } = getStoredCheckoutCustomer();
    try {
      await cancelActiveCheckoutSession({
        session_id: activeSession.sessionId,
        email: email || undefined,
      });
    } catch {
      // Best-effort — clear locally regardless
    } finally {
      clearStoredCheckoutSession();
      clearStoredCheckoutCustomer();
      setActiveSession(null);
      setIsCancellingSession(false);
    }
  };

  // ── Loading ──────────────────────────────────────────────────────────────
  if (isLoading) {
    return <LoadingLogo />;
  }

  if (
    routerState.location.pathname.endsWith('/edit') ||
    routerState.location.pathname.endsWith('/edit-external') ||
    routerState.location.pathname.endsWith('/checkout') ||
    routerState.location.pathname.endsWith('/success')
  ) {
    return <Outlet />;
  }

  // ── Error ────────────────────────────────────────────────────────────────
  if (isError || !event) {
    return (
      <div className="min-h-screen bg-black text-white pb-32">
        <Header />
        <main className="pt-24 md:pt-20 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col items-center justify-center min-h-[80vh]">
          <div className="w-full rounded-4xl border border-white/8 bg-zinc-950/90 p-8 sm:p-12 text-center shadow-2xl flex flex-col items-center gap-6">
            <div className="w-20 h-20 rounded-full flex items-center justify-center bg-red-500/10">
              <FiAlertCircle size={40} className="text-red-400" />
            </div>
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2">Event not found</h2>
              <p className="text-zinc-400 text-sm sm:text-base max-w-sm mx-auto">
                This event may have been removed, or you don't have permission to view it.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 w-full max-w-xs">
              <Button
                onClick={() => navigate({ to: '/events' })}
                size="lg"
                className="w-full rounded-full px-8"
              >
                <FiArrowLeft size={16} className="mr-2" />
                Back to Events
              </Button>
            </div>
          </div>
        </main>
        <BottomNav />
      </div>
    );
  }

  // ── Derived data ─────────────────────────────────────────────────────────
  const currentUserId = user?.id ?? localStorage.getItem('user_id') ?? '';
  const creatorId = event.event_creator_id ?? event.event_creator;
  const isOwner = !!currentUserId && !!creatorId && String(creatorId) === String(currentUserId);
  const isCollaborator =
    !!currentUserId &&
    (event.collaborators ?? []).some((c) => String(c.id) === String(currentUserId));
  const canManage = isOwner || isCollaborator;
  const canEdit = canEditEvent(event, currentUserId);
  const hidePrivateToggle = event.event_structure_type === 'multi_event' || !!event.is_sub_event;

  const title = event.event_title || 'Untitled Event';
  const location = getEventLocation(event);

  // Non-archived tickets, free first then paid — order used for the ticket selection list
  const freeTickets = (event.tickets ?? []).filter((t) => !t.is_archived && t.price === 0);
  const paidTickets = (event.tickets ?? []).filter((t) => !t.is_archived && t.price > 0);
  const displayTickets = [...freeTickets, ...paidTickets];

  const timezoneLabel = getTimezoneLabel(event.iana_timezone, event.timezone_label);

  const interestCard =
    !isOwner &&
    event.is_draft_or_published !== 'draft' &&
    !event.is_cancelled &&
    !isEventExpired(event) ? (
      <EventInterestCard
        eventId={event.id}
        eventTitle={event.event_title || 'Musicade Event'}
        imageUrl={getEventCoverImage(event)}
        isAuthenticated={isAuthenticated}
        defaultName={user?.fullName}
        defaultEmail={user?.email}
        isInterested={markedInterested || !!event.interested}
        onInterested={() => setMarkedInterested(true)}
      />
    ) : null;

  // Ticket sales window — only applies to internal ticketing
  const isInternalTicketing = event.event_ticketing_type !== 'external';
  const activeTicketsForSales = (event.tickets ?? []).filter((t) => !t.is_archived);
  const hasActiveTickets = activeTicketsForSales.some(
    (t) => getTicketSalesStatus(t) === 'available',
  );
  const hasNotStartedTickets = activeTicketsForSales.some(
    (t) => getTicketSalesStatus(t) === 'not_started',
  );

  const eventTicketSalesState: TicketSalesStatus = !isInternalTicketing
    ? 'available'
    : hasActiveTickets
      ? 'available'
      : hasNotStartedTickets
        ? 'not_started'
        : 'ended';

  const ticketCheckoutDisabled =
    event.is_cancelled ||
    event.is_draft_or_published === 'draft' ||
    eventTicketSalesState !== 'available';

  const shareButton = (
    <button
      onClick={handleShare}
      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-(--event-accent)/15 border border-(--event-accent)/30 text-white text-[13px] font-semibold hover:bg-(--event-accent)/25 transition-colors cursor-pointer"
    >
      <FiShare2 size={14} className="text-blue-300" />
      <span>Share</span>
    </button>
  );

  const statusBadges = (
    <>
      {event.is_cancelled && (
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-red-500/10 border border-red-500/30 text-red-400 text-[12px] font-medium">
          <FiX size={11} />
          Cancelled
        </span>
      )}
      {event.is_draft_or_published === 'draft' && (
        <span className="px-3 py-1 rounded-full bg-blue-500/10 border border-blue-500/30 text-blue-400 text-[12px] font-medium">
          Draft
        </span>
      )}
      {event.event_structure_type === 'single' && (
        <span className="px-3 py-1 rounded-full bg-indigo-500/10 border border-indigo-500/25 text-indigo-400 text-[12px] font-medium">
          Single Event
        </span>
      )}
      {event.event_structure_type === 'multi_event' && (
        <span className="px-3 py-1 rounded-full bg-blue-500/10 border border-blue-500/25 text-blue-400 text-[12px] font-medium">
          Multi Event
        </span>
      )}
      {event.event_structure_type === 'multi_event' && event.is_sub_event === false && (
        <span className="px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/25 text-emerald-400 text-[12px] font-medium">
          Main Event
        </span>
      )}
      {event.event_structure_type === 'multi_event' && event.is_sub_event === true && (
        <span className="px-3 py-1 rounded-full bg-purple-500/10 border border-purple-500/25 text-purple-400 text-[12px] font-medium">
          Sub Event
        </span>
      )}
      {event.is_private && (
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-zinc-800 border border-white/10 text-zinc-400 text-[12px] font-medium">
          <FiLock size={11} />
          Private
        </span>
      )}
      {event.event_ticketing_type === 'external' && event.external_ticketing_url && (
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-900/30 border border-amber-700/40 text-amber-400 text-[12px] font-medium">
          <FiExternalLink size={11} />
          External
        </span>
      )}
      {event.event_type && event.event_type.toLowerCase() !== 'physical' && (
        <span className="px-3 py-1 rounded-full bg-blue-500/10 border border-blue-500/25 text-blue-400 text-[12px] font-medium capitalize">
          {event.event_type}
        </span>
      )}
    </>
  );

  return (
    <div className="min-h-screen bg-black text-white pb-32">
      <Header />

      <main
        className="max-w-360 mx-auto px-4 sm:px-6 lg:px-8 pb-32 pt-[calc(var(--header-height,4.5rem)+1rem)] lg:pt-[calc(var(--header-height)+1.5rem)]"
        style={eventAccent.style}
      >
        {/* ── Breadcrumb navigation + actions ── */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 mt-2 min-w-0">
          <div className="flex items-center gap-2 text-[13px] min-w-0">
            <Link
              to="/events"
              className="flex items-center gap-1.5 text-white font-semibold hover:text-zinc-300 transition-colors shrink-0"
            >
              <FiArrowLeft size={16} />
              Events
            </Link>
            <FiChevronRight size={14} className="shrink-0 text-zinc-600" />
            <span className="text-zinc-500 truncate">{title}</span>
          </div>

          {/* Action buttons */}
          <div className="flex flex-wrap items-center gap-3 w-full sm:w-auto shrink-0">
            {canEdit && (
              <Button
                variant="secondary"
                className="justify-center rounded-full gap-2 px-5 bg-zinc-900 border-white/5 hover:bg-white/5"
                onClick={handleEdit}
              >
                <FiEdit2 size={15} />
                <span>Edit</span>
              </Button>
            )}
            {canManage &&
              event.is_draft_or_published !== 'draft' &&
              event.event_ticketing_type !== 'external' && (
                <button
                  onClick={() => setIsCheckInOpen(true)}
                  className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-zinc-800 border border-white/10 text-zinc-200 text-[13px] font-semibold hover:bg-zinc-700 transition-colors cursor-pointer sm:py-2"
                >
                  <FiCheckSquare size={14} />
                  <span>Check-in</span>
                </button>
              )}
            {/* Share — same line as the other action buttons on mobile; re-rendered inline with badges on desktop */}
            <div className="sm:hidden">{shareButton}</div>
            {canManage &&
              !hidePrivateToggle &&
              event.is_private &&
              event.is_draft_or_published === 'published' &&
              !event.is_cancelled && (
                <button
                  onClick={() => setIsManagePrivateAccessOpen(true)}
                  className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-zinc-800 border border-white/10 text-zinc-200 text-[13px] font-semibold hover:bg-zinc-700 transition-colors cursor-pointer sm:py-2"
                >
                  <FiLock size={14} />
                  <span>Invite & Share</span>
                </button>
              )}
            {isOwner && event.is_draft_or_published === 'published' && !event.is_cancelled && (
              <button
                onClick={() => setIsManageCollaboratorsOpen(true)}
                className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-zinc-800 border border-white/10 text-zinc-200 text-[13px] font-semibold hover:bg-zinc-700 transition-colors cursor-pointer sm:py-2"
              >
                <FiUsers size={14} />
                <span>Collaborators</span>
              </button>
            )}
          </div>
        </div>

        {/* ── Two-column grid ── */}
        <div className="grid grid-cols-1 lg:grid-cols-[3fr_2fr] gap-8 lg:gap-10 items-start">
          {/* ── LEFT COLUMN ── */}
          <div className="space-y-6 min-w-0">
            {/* Image Carousel */}
            <div className="space-y-4">
              <div
                className="w-full rounded-2xl overflow-hidden bg-zinc-950 relative py-6 sm:py-10"
                onTouchStart={handleTouchStart}
                onTouchEnd={handleTouchEnd}
              >
                {images.length > 0 ? (
                  <>
                    <div
                      aria-hidden="true"
                      className="absolute inset-0 bg-cover bg-center scale-110 blur-2xl opacity-40"
                      style={{ backgroundImage: `url(${images[imageIndex]})` }}
                    />
                    <div aria-hidden="true" className="absolute inset-0 bg-black/40" />
                    <img
                      src={images[imageIndex]}
                      alt={title}
                      className="relative block max-w-full max-h-[60vh] object-contain mx-auto rounded-xl shadow-[0_30px_70px_-15px_rgba(0,0,0,0.85)]"
                      onError={(event) => {
                        const img = event.currentTarget;
                        if (img.dataset.fallbackApplied === 'true') return;
                        img.dataset.fallbackApplied = 'true';
                        img.src = EVENT_IMAGE_FALLBACK_SRC;
                        img.className =
                          'relative block max-w-full max-h-[60vh] object-contain mx-auto rounded-xl shadow-[0_30px_70px_-15px_rgba(0,0,0,0.85)] p-8 bg-zinc-900';
                      }}
                    />

                    {/* Carousel navigation */}
                    {images.length > 1 && (
                      <>
                        <button
                          onClick={() =>
                            setImageIndex((i) => (i - 1 + images.length) % images.length)
                          }
                          className="absolute left-3 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/60 backdrop-blur-sm flex items-center justify-center text-white hover:bg-black/80 transition-colors"
                        >
                          <FiChevronLeft size={18} />
                        </button>
                        <button
                          onClick={() => setImageIndex((i) => (i + 1) % images.length)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/60 backdrop-blur-sm flex items-center justify-center text-white hover:bg-black/80 transition-colors"
                        >
                          <FiChevronRight size={18} />
                        </button>
                        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                          {images.map((_, i) => (
                            <button
                              key={i}
                              onClick={() => setImageIndex(i)}
                              className={`w-1.5 h-1.5 rounded-full transition-all ${
                                i === imageIndex ? 'bg-white scale-125' : 'bg-white/40'
                              }`}
                            />
                          ))}
                        </div>
                      </>
                    )}

                    {/* Badges overlay */}
                    {event.is_cancelled && (
                      <div className="absolute top-3 left-3 px-3 py-1 rounded-full bg-red-600 text-white text-[12px] font-semibold">
                        Cancelled
                      </div>
                    )}
                    {event.is_private && (
                      <div className="absolute top-3 right-3 p-2 rounded-full bg-black/60 backdrop-blur-sm text-zinc-300">
                        <FiLock size={13} />
                      </div>
                    )}
                  </>
                ) : (
                  <div className="w-full aspect-video border border-white/8 rounded-2xl flex items-center justify-center text-zinc-700">
                    <FiMusic size={56} />
                  </div>
                )}
              </div>
            </div>

            {/* Title */}
            <h1 className="text-white text-3xl sm:text-4xl lg:text-[40px] font-bold leading-tight tracking-tight min-w-0">
              {title}
            </h1>

            {/* Status badges + Share — same horizontal line on desktop, badges first */}
            <div className="hidden sm:flex sm:flex-wrap sm:items-center gap-2">
              {statusBadges}
              {shareButton}
            </div>

            {/* Status badges — standalone row on mobile (Share already rendered at the top) */}
            <div className="flex flex-wrap gap-2 sm:hidden">{statusBadges}</div>

            {/* About */}
            {event.info && (
              <div>
                <h2 className="text-white text-xl font-semibold mb-3">About this Event</h2>
                <p className="text-zinc-400 text-[15px] leading-relaxed whitespace-pre-line">
                  {event.info}
                </p>
              </div>
            )}

            {/* Start Date / End Date */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="bg-zinc-950 rounded-2xl p-4">
                <p className="text-zinc-500 text-[11px] uppercase tracking-wider font-medium mb-2">
                  Start Date
                </p>
                <p className="text-white text-[14px] font-semibold flex items-center gap-1.5 flex-wrap">
                  <FiCalendar size={13} className="text-(--event-accent) shrink-0" />
                  <span>{formatDateOrdinal(event.start, event.timezone, true)}</span>
                  <span className="text-zinc-600 font-normal">•</span>
                  <span className="text-zinc-400 font-medium">
                    {formatTime(event.start, event.timezone, true, timezoneLabel)}
                  </span>
                </p>
              </div>
              <div className="bg-zinc-950 rounded-2xl p-4">
                <p className="text-zinc-500 text-[11px] uppercase tracking-wider font-medium mb-2">
                  End Date
                </p>
                {event.end ? (
                  <p className="text-white text-[14px] font-semibold flex items-center gap-1.5 flex-wrap">
                    <FiCalendar size={13} className="text-(--event-accent) shrink-0" />
                    <span>{formatDateOrdinal(event.end, event.timezone, true)}</span>
                    <span className="text-zinc-600 font-normal">•</span>
                    <span className="text-zinc-400 font-medium">
                      {formatTime(event.end, event.timezone, true, timezoneLabel)}
                    </span>
                  </p>
                ) : (
                  <p className="text-zinc-500 text-[13px]">Not specified</p>
                )}
              </div>
            </div>

            {/* Location */}
            {(location || (event.city && event.country)) && (
              <div className="space-y-4">
                <div
                  className="rounded-2xl border border-white/10 backdrop-blur-[28px] overflow-hidden"
                  style={{
                    background:
                      'linear-gradient(90.08deg, rgba(255, 255, 255, 0.1) 42.32%, rgba(255, 255, 255, 0) 156.33%)',
                  }}
                >
                  <div className="flex items-center gap-4 p-3">
                    <div className="w-11 h-11 rounded-full bg-(--event-accent)/15 flex items-center justify-center shrink-0">
                      <FiMapPin size={18} className="text-(--event-accent)" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-[15px] font-semibold">Location</p>
                      <p className="text-zinc-400 text-[13px] truncate">
                        {location ||
                          [event.city, event.state, event.country].filter(Boolean).join(', ')}
                      </p>
                    </div>
                    {location && (
                      <a
                        href={`https://maps.google.com/?q=${encodeURIComponent(location)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Get directions"
                        className="h-10 px-3 sm:px-4 rounded-full bg-zinc-800 border border-white/5 flex items-center justify-center gap-2 shrink-0 hover:bg-zinc-700 transition-colors"
                      >
                        <span className="hidden sm:inline text-white text-[13px] font-semibold whitespace-nowrap">
                          Get direction
                        </span>
                        <img src={locationDirectionIcon} alt="" className="w-3 h-3" />
                      </a>
                    )}
                  </div>

                  {/* Map embed (desktop/web only) */}
                  {(() => {
                    const coords = parseWKTPoint(event.location);
                    const query = coords
                      ? `${coords.lat},${coords.lng}`
                      : [location, event.city, event.state, event.country]
                          .filter(Boolean)
                          .join(',');
                    if (!query) return null;
                    return (
                      <div className="hidden sm:block p-3 pt-0">
                        <div className="w-full h-56 rounded-xl overflow-hidden">
                          <iframe
                            title="Event location map"
                            className="w-full h-full"
                            loading="lazy"
                            referrerPolicy="no-referrer-when-downgrade"
                            src={`https://www.google.com/maps/embed/v1/place?key=${GOOGLE_MAPS_API_KEY}&q=${encodeURIComponent(query)}&zoom=13`}
                          />
                        </div>
                      </div>
                    );
                  })()}
                </div>

                {(event.directions ?? (event as any).location_directions) && (
                  <div className="bg-zinc-950 border border-white/8 rounded-3xl p-5">
                    <p className="text-zinc-500 text-[11px] uppercase tracking-wider font-medium mb-1.5">
                      Directions
                    </p>
                    <p className="text-zinc-300 text-[14px] leading-relaxed whitespace-pre-line">
                      {event.directions ?? (event as any).location_directions}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Active Session Banner */}
            {activeSession && (
              <div className="p-4 bg-amber-500/8 border border-amber-500/30 rounded-2xl">
                <div className="flex items-start gap-3 mb-4">
                  <FiAlertCircle className="text-amber-400 mt-0.5 shrink-0" size={18} />
                  <div>
                    <p className="text-amber-300 font-semibold text-[14px] mb-0.5">
                      Active Checkout Session
                    </p>
                    <p className="text-zinc-400 text-[13px]">
                      You have an unfinished checkout session. Resume to continue payment or cancel
                      to release your reserved tickets.
                    </p>
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  <button
                    onClick={handleResumeSession}
                    className="w-full inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-blue-600/20 hover:bg-blue-600/30 border border-blue-500/30 text-blue-300 text-[13px] font-medium transition-colors"
                  >
                    <FiArrowRight size={14} />
                    Resume Session
                  </button>
                  <button
                    onClick={handleCancelActiveSession}
                    disabled={isCancellingSession}
                    className="w-full inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-red-500/10 hover:bg-red-500/20 border border-red-500/25 text-red-400 text-[13px] font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <FiX size={14} />
                    {isCancellingSession ? 'Cancelling...' : 'Cancel Session'}
                  </button>
                </div>
              </div>
            )}

            {/* Sub-events */}
            {isMultiEvent && (
              <div>
                <h2 className="text-white text-[11px] font-bold uppercase tracking-widest flex items-center gap-2 mb-3">
                  <FiList size={11} />
                  Sub Events
                </h2>
                {subEventsLoading ? (
                  <div className="space-y-3">
                    {[0, 1, 2].map((i) => (
                      <div
                        key={i}
                        className="bg-zinc-900/60 border border-white/8 rounded-2xl p-4 animate-pulse"
                      >
                        <div className="h-4 bg-zinc-800 rounded w-2/3 mb-2" />
                        <div className="h-3 bg-zinc-800 rounded w-1/3" />
                      </div>
                    ))}
                  </div>
                ) : (subEvents ?? []).length === 0 ? (
                  <div className="bg-zinc-900/40 border border-white/5 rounded-2xl p-4 text-zinc-500 text-[13px] text-center">
                    No sub-events yet
                  </div>
                ) : (
                  <div className="space-y-3">
                    {(subEvents ?? []).map((sub) => {
                      const canEditSub = canEditEvent(sub, currentUserId);
                      return (
                        <div
                          key={sub.id}
                          className="flex items-center gap-4 bg-zinc-900/60 border border-white/8 rounded-2xl p-4 hover:border-white/20 hover:bg-zinc-900 transition-all group"
                        >
                          <Link
                            to="/events/$eventId"
                            params={{ eventId: String(sub.id) }}
                            className="flex items-center gap-4 flex-1 min-w-0"
                          >
                            {/* Thumbnail */}
                            <div className="w-14 h-14 rounded-xl overflow-hidden shrink-0 bg-zinc-800 border border-white/5">
                              {sub.medias?.[0]?.file_url || sub.featured_image?.file_url ? (
                                <img
                                  src={
                                    sub.medias?.[0]?.file_url ?? sub.featured_image?.file_url ?? ''
                                  }
                                  alt={sub.event_title}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center text-zinc-700">
                                  <FiMusic size={20} />
                                </div>
                              )}
                            </div>

                            {/* Info */}
                            <div className="flex-1 min-w-0">
                              <p className="text-white font-semibold text-[14px] truncate group-hover:text-blue-300 transition-colors">
                                {sub.event_title || 'Untitled Sub Event'}
                              </p>
                              {sub.start && (
                                <p className="text-zinc-400 text-[12px] mt-0.5 flex items-center gap-1">
                                  <FiCalendar size={11} />
                                  {formatDate(sub.start)} · {formatTime(sub.start, sub.timezone)}
                                </p>
                              )}
                              {sub.venue && (
                                <p className="text-zinc-500 text-[12px] mt-0.5 flex items-center gap-1 truncate">
                                  <FiMapPin size={11} />
                                  {sub.venue}
                                </p>
                              )}
                            </div>
                          </Link>

                          {canEditSub && (
                            <Link
                              to={getEditRoute(sub)}
                              params={{ eventId: String(sub.id) }}
                              aria-label={`Edit ${sub.event_title || 'sub-event'}`}
                              className="shrink-0 p-2 rounded-full text-zinc-500 hover:text-white hover:bg-white/10 transition-colors"
                            >
                              <FiEdit2 size={14} />
                            </Link>
                          )}

                          {sub.is_cancelled ? (
                            <span className="shrink-0 px-2 py-0.5 rounded-full bg-red-500/10 border border-red-500/25 text-red-400 text-[11px] font-medium">
                              Cancelled
                            </span>
                          ) : (
                            <Link
                              to="/events/$eventId"
                              params={{ eventId: String(sub.id) }}
                              aria-label={`Open ${sub.event_title || 'sub-event'}`}
                              className="shrink-0 p-2 rounded-full text-zinc-600 hover:text-blue-400 hover:bg-white/5 transition-colors"
                            >
                              <FiArrowRight size={14} />
                            </Link>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ── RIGHT COLUMN (Sidebar) ── */}
          <div className="lg:sticky lg:top-[calc(var(--header-height,5.5rem)+1.25rem)] flex flex-col gap-6">
            {isInternalTicketing ? (
              <>
                {/* Tickets + order summary (tablet/desktop) */}
                {displayTickets.length > 0 && (
                  <div className="hidden sm:block">
                    <h2 className="text-white text-base font-semibold mb-3 flex items-center gap-2">
                      <span className="w-1 h-4 rounded-full bg-(--event-accent) inline-block" />
                      Tickets
                    </h2>
                    <div className="space-y-3">
                      {displayTickets.map((ticket) =>
                        ticket.id !== undefined ? (
                          <TicketSelectionCard
                            key={ticket.id}
                            ticket={ticket}
                            quantity={ticketCart[ticket.id] ?? 0}
                            onAdd={addToCart}
                            onRemove={removeFromCart}
                            timezone={event.timezone}
                            disabled={event.is_cancelled || event.is_draft_or_published === 'draft'}
                          />
                        ) : null,
                      )}
                    </div>
                  </div>
                )}

                {displayTickets.length > 0 && (
                  <div className="hidden sm:block">
                    <OrderSummaryCard
                      tickets={displayTickets}
                      ticketCounts={ticketCart}
                      onCheckout={handleProceedToCheckout}
                      checkoutDisabled={ticketCheckoutDisabled}
                      floatingFooter={interestCard}
                    />
                  </div>
                )}

                {/* Interest card: floats under checkout on desktop; inline otherwise */}
                {interestCard && (
                  <div
                    className={
                      displayTickets.length > 0 ? 'sm:hidden empty:hidden' : 'empty:hidden'
                    }
                  >
                    {interestCard}
                  </div>
                )}

                {/* Buy Ticket (mobile) — opens the ticket selection modal */}
                {displayTickets.length > 0 && !isTicketModalOpen && (
                  <div className="sm:hidden fixed inset-x-0 bottom-[calc(env(safe-area-inset-bottom)+7rem)] z-60 px-4 pointer-events-none">
                    <button
                      type="button"
                      onClick={() => setIsTicketModalOpen(true)}
                      disabled={ticketCheckoutDisabled}
                      className="pointer-events-auto w-full flex items-center justify-center px-4 py-3 rounded-lg bg-(--event-accent) text-(--event-accent-fg) hover:brightness-110 active:brightness-95 text-[14px] font-semibold shadow-2xl shadow-black/30 ring-1 ring-black/10 transition-[filter] disabled:bg-zinc-700 disabled:text-zinc-400 disabled:shadow-none disabled:ring-0 disabled:cursor-not-allowed"
                    >
                      Buy Ticket
                    </button>
                  </div>
                )}
              </>
            ) : (
              event.external_ticketing_url && (
                <div className="p-4 sm:p-5 bg-zinc-900 border border-white/10 rounded-3xl flex flex-col sm:flex-row sm:items-center gap-4">
                  <div className="flex items-center gap-4 flex-1 min-w-0">
                    <div className="w-14 h-14 shrink-0 rounded-full bg-zinc-800 flex items-center justify-center">
                      <LuTicket className="text-white" size={24} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-[#F0866B] font-semibold text-[16px] leading-tight">
                        This event is hosted externally
                      </p>
                      <p className="mt-1 text-zinc-400 text-[14px] leading-snug">
                        Ticket sales and event details are managed by the organizer, not Musicade
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={externalTicketFlow.open}
                    className="shrink-0 inline-flex items-center justify-center gap-2 h-12 px-6 rounded-full bg-white hover:bg-zinc-200 text-black text-[14px] font-semibold transition-colors"
                  >
                    Get Tickets
                    <FiExternalLink size={16} />
                  </button>
                </div>
              )
            )}

            {/* Event Organizer (hidden for drafts and for the event's own host) */}
            {!isOwner &&
              event.is_draft_or_published !== 'draft' &&
              (() => {
                const creator =
                  event.host ?? event.event_creator_details ?? event.event_creator_profile;
                const routeId = creator?.id ?? creatorId;
                return creator ? (
                  <EventOrganizerCard
                    creator={creator}
                    profileId={routeId}
                    currentUserId={currentUserId}
                    isAuthenticated={isAuthenticated}
                    associatedCreators={event.host?.associated_creators ?? []}
                  />
                ) : null;
              })()}

            {/* Inspired to host CTA */}
            {!isOwner && event.is_draft_or_published !== 'draft' && (
              <div className="relative overflow-hidden rounded-3xl border border-white/8 p-5 bg-gradient-to-br from-indigo-950 via-zinc-950 to-black">
                <p className="text-white text-[15px] font-semibold mb-1">
                  Inspired to host your own event?
                </p>
                <p className="text-zinc-400 text-[13px] mb-4">
                  Bring your next event to life with Musicade.
                </p>
                <button
                  type="button"
                  onClick={() => {
                    if (!isAuthenticated) {
                      setIsCreateAuthGateOpen(true);
                      return;
                    }
                    navigate({ to: '/events/create' });
                  }}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white text-black text-[13px] font-semibold hover:bg-zinc-200 transition-colors cursor-pointer"
                >
                  Start Creating
                  <FiArrowRight size={14} />
                </button>
              </div>
            )}

            {/* Collaborators — shown here only for drafts, where the organizer
                card (which folds collaborators in as "Associated Creators")
                is hidden */}
            {event.is_draft_or_published === 'draft' && (event.collaborators ?? []).length > 0 && (
              <div>
                <p className="text-white text-[11px] font-bold uppercase tracking-widest flex items-center gap-2 mb-3">
                  <FiUsers size={11} />
                  Collaborators
                </p>
                <div className="bg-zinc-950 border border-white/8 rounded-3xl overflow-hidden p-5">
                  <div className="flex flex-wrap gap-3">
                    {event.collaborators!.map((c) => {
                      const hasUsername = Boolean(c.username && c.username.trim());
                      const email = (c as any).email || (c as any).invitee_email;
                      const displayName = hasUsername
                        ? (c.full_name?.split(' ')[0] ?? c.username)
                        : email || 'Invited User';

                      return (
                        <div
                          key={c.id}
                          className="flex items-center gap-2.5 p-2 pr-3 rounded-full bg-zinc-900 border border-white/8 transition-all group"
                        >
                          <Avatar
                            src={c.prof_pics}
                            alt={c.username || email || ''}
                            name={displayName || 'User'}
                            size="sm"
                          />
                          {hasUsername ? (
                            <Link
                              to="/profile/$identifier"
                              params={{
                                identifier: getProfileIdentifier({
                                  id: c.id,
                                  username: c.username!,
                                }),
                              }}
                              className="text-zinc-300 text-[13px] font-medium group-hover:text-white transition-colors truncate max-w-28 hover:underline"
                            >
                              {displayName}
                            </Link>
                          ) : (
                            <span className="text-zinc-300 text-[13px] font-medium truncate max-w-36">
                              {displayName}
                            </span>
                          )}
                          {c.role && (
                            <span className="px-2 py-0.5 rounded-full bg-zinc-700 border border-white/10 text-zinc-400 text-[11px] font-medium shrink-0">
                              {c.role}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      {isCheckInOpen && (
        <EventCheckInModal
          event={event}
          isOpen={isCheckInOpen}
          onClose={() => setIsCheckInOpen(false)}
        />
      )}

      {isManagePrivateAccessOpen && (
        <ManagePrivateAccessModal
          eventId={eventId}
          isOpen={isManagePrivateAccessOpen}
          onClose={() => setIsManagePrivateAccessOpen(false)}
        />
      )}

      {isManageCollaboratorsOpen && (
        <ManageCollaboratorsModal
          eventId={eventId}
          eventType={getCollaboratorEventType(event)}
          isOpen={isManageCollaboratorsOpen}
          onClose={() => setIsManageCollaboratorsOpen(false)}
        />
      )}

      <TicketSelectionModal
        isOpen={isTicketModalOpen}
        onClose={() => setIsTicketModalOpen(false)}
        tickets={displayTickets}
        ticketCounts={ticketCart}
        onAdd={addToCart}
        onRemove={removeFromCart}
        timezone={event.timezone}
        disabled={event.is_cancelled || event.is_draft_or_published === 'draft'}
        onCheckout={() => {
          setIsTicketModalOpen(false);
          handleProceedToCheckout();
        }}
        checkoutDisabled={ticketCheckoutDisabled}
        accentStyle={eventAccent.style}
      />

      {externalTicketFlow.modals}
      <ShareEventModal
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        shareUrl={getEventShareUrl(event)}
        shareTitle={event.event_title || 'Musicade Event'}
      />

      {isCreateAuthGateOpen && (
        <AuthGateModal
          icon={<img src="/Musicade-Logo.png" alt="" className="w-10 h-10 object-contain" />}
          title="Create Your First Event"
          description="Create a free account to start hosting your own events and selling tickets on Musicade."
          proofText="Join thousands of creators already hosting events on Musicade."
          ctaLabel="Create Account & Start Hosting"
          onClose={() => setIsCreateAuthGateOpen(false)}
        />
      )}

      <BottomNav />
    </div>
  );
}
